/**
 * Login Page
 *
 * Authentication page with username/password and Google reCAPTCHA.
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { useAuth, useToast } from '@/hooks';
import { executeRecaptcha } from '@/utils/recaptcha';
import { APP_INFO } from '@/config/app.config';

interface LocationState {
  from?: {
    pathname: string;
  };
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuth();
  const toast = useToast();

  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Get the redirect path from location state
  const from = (location.state as LocationState)?.from?.pathname || '/dashboard';

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    clearError();

    // Validate form
    if (!username.trim()) {
      setFormError('Username is required');
      return;
    }

    if (!password.trim()) {
      setFormError('Password is required');
      return;
    }

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha('login');

      // Attempt login
      await login({
        username: username.trim(),
        password: password.trim(),
        recaptchaToken,
      });

      // Success - show toast and redirect
      toast.success('Login successful');
      navigate(from, { replace: true });
    } catch (err) {
      // Error handling
      const message = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setFormError(message);
      toast.error(message);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h1 className="text-4xl font-bold mb-4">{APP_INFO.name}</h1>
          <p className="text-lg opacity-90 mb-8">{APP_INFO.description}</p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 rounded-full bg-white" />
              <span>Manage organizations and users</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 rounded-full bg-white" />
              <span>Track subscription plans</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 rounded-full bg-white" />
              <span>Monitor login audit history</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex w-full lg:w-1/2 items-center justify-center p-8 bg-background dark:bg-background-dark">
        <div className="w-full max-w-md">
          {/* Mobile branding */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-2xl font-bold text-primary">{APP_INFO.name}</h1>
            <p className="text-sm text-foreground-muted mt-1">Admin Dashboard</p>
          </div>

          {/* Login form card */}
          <div className="bg-card dark:bg-card-dark rounded-xl shadow-card p-8 border border-border dark:border-border-dark">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold text-foreground dark:text-foreground-dark">
                Welcome back
              </h2>
              <p className="text-foreground-muted dark:text-foreground-dark-muted mt-1">
                Sign in to your account to continue
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error message */}
              {(formError || error) && (
                <div className="p-3 rounded-md bg-danger-50 border border-danger-200 text-danger-700 text-sm">
                  {formError || error}
                </div>
              )}

              {/* Username field */}
              <Input
                label="Username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
                autoComplete="username"
                required
              />

              {/* Password field */}
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="current-password"
                required
                rightElement={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-foreground-muted hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                }
              />

              {/* Submit button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                loading={isLoading}
                leftIcon={<LogIn className="h-4 w-4" />}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            {/* reCAPTCHA notice */}
            <p className="mt-6 text-center text-xs text-foreground-muted dark:text-foreground-dark-muted">
              This site is protected by reCAPTCHA and the Google{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </div>

          {/* Version info */}
          <p className="mt-6 text-center text-xs text-foreground-muted dark:text-foreground-dark-muted">
            Version {APP_INFO.version}
          </p>
        </div>
      </div>
    </div>
  );
}
